<template>
  <div class="pt-4 pb-24 px-4">
    <h3 class="text-3xl font-bold mb-5">{{ $t('settings') }}</h3>
    <template v-if="loading">
      <div class="rounded-lg" :class="[$theme.cardBg]">
        <LoadingBlock />
      </div>
    </template>
    <template v-else>
      <DisconnectExchangeAlert :exchanges="exchanges" />

      <div class="mb-3 border-b pb-3" :class="[$theme.cardBorderColor]">
        <div>
          <router-link to="/setting/profile" class="flex items-center justify-between py-3">
            <div class="flex items-center">
              <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting]">
                <UserIcon class="w-5" :class="[$theme.iconBgSettingTextColor]" />
              </div>
              <span class="ml-2">{{ $t('my_profile') }}</span>
            </div>
            <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
            </div>
          </router-link>
        </div>
        <div v-if="isEnabledPaymentCards">
          <router-link to="/setting/payment-cards" class="flex items-center justify-between py-3">
            <div class="flex items-center">
              <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting]">
                <CreditCardIcon class="w-5" :class="[$theme.iconBgSettingTextColor]" />
              </div>
              <span class="ml-2">{{ $t('payment_cards') }}</span>
            </div>
            <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
            </div>
          </router-link>
        </div>
      </div>

      <div class="border-b pb-3" :class="[$theme.cardBorderColor]">
        <div v-for="exchange in exchanges" :key="exchange.id">
          <router-link :to="`/exchange/${exchange.id}`" class="flex items-center justify-between py-3">
            <div class="flex items-center">
              <template v-if="exchange.exchange_slug === 'binance'">
                <img src="/img/binance.png" class="w-6 mr-2" />
                Binance
              </template>
              <template v-if="exchange.exchange_slug === 'okx'">
                <img src="/img/okx.png" class="w-6 mr-2" />
                OKX
              </template>
              <template v-if="exchange.exchange_slug === 'bybit'">
                <img src="/img/bybit.png" class="w-6 mr-2 rounded-full" />
                Bybit
              </template>
              <template v-if="exchange.exchange_slug === 'mexc'">
                <img src="/img/mexc.png" class="w-6 mr-2 rounded-full" />
                Mexc
              </template>
              <template v-if="exchange.exchange_slug === 'whitebit'">
                <img src="/img/whitebit.png" class="w-6 mr-2 rounded-full" />
                Whitebit
              </template>
              <span class="ml-2" :class="[$theme.cardLabelTextColor]">ID: {{ exchange.id }}</span>
            </div>
            <div>
              <template v-if="!exchange.status_connected">
                <ExclamationTriangleIcon class="w-5 h-5" :class="[$theme.navActiveTextColor2]"></ExclamationTriangleIcon>
              </template>
            </div>
          </router-link>
        </div>
        <router-link to="/create-exchange" class="flex items-center py-3" :class="[$theme.navActiveTextColor2]">
          <div class="flex items-center">
            <PlusIcon class="w-6 mr-2" /> {{ $t('add_exchange_account') }}
          </div>
        </router-link>
      </div>

      <div class="mt-3 border-b pb-3" :class="[$theme.cardBorderColor]">
        <div>
          <div>
            <router-link to="/setting/signals" class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                  <SignalIcon class="w-5" />
                </div>
                <span class="ml-2">{{ $t('signals') }}</span>
              </div>
              <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
                <ChevronRightIcon class="w-5" />
              </div>
            </router-link>
          </div>
          <div>
            <router-link to="/setting/notifications" class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                  <NotificationIcon class="w-5" />
                </div>
                <span class="ml-2">{{ $t('notifications') }}</span>
              </div>
              <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
                <ChevronRightIcon class="w-5" />
              </div>
            </router-link>
          </div>
          <div>
            <router-link to="/setting/language" class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                  <TranslateIcon class="w-5" />
                </div>
                <span class="ml-2">{{ $t('language') }}</span>
              </div>
              <div class="flex items-center" :class="[$theme.cardLabelTextColor]">
                {{ selectedLanguageTitle }}
                <ChevronRightIcon class="w-5" />
              </div>
            </router-link>
          </div>
          <div>
            <router-link to="/setting/security" class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                  <SecurityIcon class="w-5" />
                </div>
                <span class="ml-2">{{ $t('security') }}</span>
              </div>
              <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
                <ChevronRightIcon class="w-5" />
              </div>
            </router-link>
          </div>
          <div>
            <router-link to="/setting/logs" class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                  <LogsIcon class="w-5" />
                </div>
                <span class="ml-2">{{ $t('logs') }}</span>
              </div>
              <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
                <ChevronRightIcon class="w-5" />
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <div class="mt-3 border-b pb-3" :class="[$theme.cardBorderColor]">
        <div>
          <a href="#" @click="onChangeTheme" class="flex items-center justify-between py-3">
            <div class="flex items-center">
              <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                <BeakerIcon class="w-5"  :class="[$theme.iconBgSettingFillColor]" />
              </div>
              <span class="ml-2">{{ $t('design') }}</span>
            </div>
            <div class="flex items-center" :class="[$theme.cardLabelTextColor]">
              {{ $user.theme === 'light' ? $t('light') : $t('dark') }}
              <ChevronRightIcon class="w-5" />
            </div>
          </a>
        </div>
      </div>

      <div class="mt-3 border-b pb-3" :class="[$theme.cardBorderColor]">
        <div>
          <router-link to="/terms" class="flex items-center justify-between py-3">
            <div class="flex items-center">
              <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                <DocumentTextIcon class="w-5" :class="[$theme.iconBgSettingFillColor]" />
              </div>
              <span class="ml-2">{{ $t('terms') }}</span>
            </div>
            <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
              <ChevronRightIcon class="w-5" />
            </div>
          </router-link>
        </div>
      </div>

      <template v-if="$settings.support_links">
        <div class="mt-3 rounded-lg pb-3">
          <div v-for="(supportLink, supportLinkIdx) in $settings.support_links" :key="supportLinkIdx">
            <a :href="supportLink.url" class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting, $theme.iconBgSettingFillColor]">
                  <QuestionMarkCircleIcon class="w-5" :class="[$theme.iconBgSettingFillColor]"/>
                </div>
                <span class="ml-2">{{ supportLink.title[$user.language_code] }}</span>
              </div>
              <div class="flex items-center text-sm" :class="[$theme.cardLabelTextColor]">
                <ChevronRightIcon class="w-5" />
              </div>
            </a>
          </div>
        </div>
      </template>

      <div v-if="user && parseInt(user.id) === 1" class="mt-6">
        <router-link to="/logs" class="block px-6 py-3 bg-gray-200 text-red-500 rounded-lg text-center">System logs</router-link>
      </div>
    </template>
  </div>

  <TransitionRoot as="template" :show="isOpenChangeTheme">
    <Dialog class="relative z-50" @close="isOpenChangeTheme = false">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      <div class="fixed inset-0 z-50 flex items-end justify-center">
        <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-300"
            enter-from="translate-y-full opacity-0"
            enter-to="translate-y-0 opacity-100"
            leave="transform transition ease-in-out duration-300"
            leave-from="translate-y-0 opacity-100"
            leave-to="translate-y-full opacity-0"
        >
          <DialogPanel
              class="w-full rounded-t-2xl px-4 pt-5 pb-4 text-left shadow-xl sm:max-w-lg sm:w-full sm:p-6"
              :class="[$theme.secondBaseBg]"
          >
            <DialogTitle as="h3" class="text-sm leading-6" :class="[$theme.baseTextColor]">
              {{ $t("select_theme") }}
            </DialogTitle>
            <div class="grid grid-cols-2 gap-4 mt-4">
              <a href="#" @click="updateTheme('light')" class="flex items-center justify-between py-3 px-4 rounded-xl cursor-pointer relative" :class="[$theme.cardBg]">
                <CheckCircleIcon
                    :class="[
                    $user.theme !== 'light' ? 'invisible' : '',
                    'h-5 w-5 absolute top-2 right-2',
                    $theme.navActiveTextColor2,
                  ]"
                    aria-hidden="true"
                />
                <div class="flex items-center relative">
                  <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting]">
                    <PhotoIcon class="w-5" :class="[$theme.iconBgSettingFillColor]" />
                  </div>
                  <span class="ml-2" :class="[$theme.baseTextColor]">{{ $t('light') }}</span>
                </div>
              </a>
              <a href="#" @click="updateTheme('dark')" class="flex items-center justify-between py-3 px-4 rounded-xl cursor-pointer relative" :class="[$theme.cardBg]">
                <CheckCircleIcon
                    :class="[
                    $user.theme !== 'dark' ? 'invisible' : '',
                    'h-5 w-5 absolute top-2 right-2',
                    $theme.navActiveTextColor2,
                  ]"
                    aria-hidden="true"
                />
                <div class="flex items-center relative">
                  <div class="w-8 h-8 flex items-center justify-center rounded-full" :class="[$theme.iconBgSetting]">
                    <PhotoIcon class="w-5" :class="[$theme.iconBgSettingFillColor]" />
                  </div>
                  <span class="ml-2" :class="[$theme.baseTextColor]">{{ $t('dark') }}</span>
                </div>
              </a>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import LoadingBlock from '@/components/LoadingBlock.vue'
import TranslateIcon from '@/assets/icons/TranslateIcon.vue'
import NotificationIcon from '@/assets/icons/NotificationIcon.vue'
import SignalIcon from '@/assets/icons/SignalIcon.vue'
import LogsIcon from '@/assets/icons/LogsIcon.vue'
import SecurityIcon from '@/assets/icons/SecurityIcon.vue'
import {
  PlusIcon,
  ChevronRightIcon,
} from '@heroicons/vue/24/outline'
import {
  ExclamationTriangleIcon,
  UserIcon,
  CreditCardIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  BeakerIcon,
  PhotoIcon,
} from "@heroicons/vue/24/solid";
import {addBackButton, hideBackButton} from "@/helpers/telegramHelper";
import DisconnectExchangeAlert from "@/components/elem/DisconnectExchangeAlert.vue";
import {CheckCircleIcon} from "@heroicons/vue/20/solid";

export default {
  components: {
    CheckCircleIcon,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    DisconnectExchangeAlert,
    PlusIcon,
    LoadingBlock,
    ChevronRightIcon,
    SignalIcon,
    UserIcon,
    ExclamationTriangleIcon,
    DocumentTextIcon,
    QuestionMarkCircleIcon,
    CreditCardIcon,
    TranslateIcon,
    NotificationIcon,
    LogsIcon,
    SecurityIcon,
    BeakerIcon,
    PhotoIcon
  },

  computed: {
    selectedLanguageTitle() {
      for (const lang of this.languages) {
        if (this.$user.language_code === lang.language_code) {
          return lang.native_title;
        }
      }

      return '';
    }
  },

  methods: {
    onChangeTheme() {
      console.log('Change theme', this.$user);

      this.isOpenChangeTheme = true
    },

    updateTheme(theme) {
      this.$api.post('/user/update-theme', {
        theme: theme,
      }).then((result) => {
        console.log(result.data)
        window.location.reload()
      }).catch(e => {
        alert(e.message)
        this.error = e
      })
    },

    loadLanguages() {
      this.$api.get('/languages').then((result) => {
        console.log(result.data.data)
        this.languages = result.data.data;
      }).catch(e => {
        alert(e.message)
        this.error = e
      })
    },

    loadExchanges() {
      this.$api.get('/exchanges').then((result) => {
        this.exchanges = result.data.data
        this.loading = false
      }).catch(e => {
        alert(e.message)
        this.error = e
      })
    }
  },

  data() {
    return {
      loading: true,
      user: null,
      exchanges: [],
      languages: [],
      error: null,
      isOpenChangeTheme: false,
      isEnabledPaymentCards: false,
    }
  },

  mounted() {
    console.log('Setting View.');

    this.loadExchanges();
    this.loadLanguages();
    addBackButton(this.$router);
  },

  beforeUnmount() {
    hideBackButton();
  }
}
</script>
